// src/Components/LoadingOverlay.tsx
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './LoadingOverlay.css';
// Import the logo directly
import bidbroLogo from '../assets/images/logo/bidbro-logo.png';

interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  isVisible: boolean;
  /** Main loading text - can be string or array for rotating text */
  text?: string | string[];
  /** Subtitle text - can be string or array for rotating text */
  subtext?: string | string[];
  /** Interval in milliseconds for text rotation (default: 3000ms) */
  textInterval?: number;
  /** Size of the logo container */
  size?: 'small' | 'medium' | 'large';
  /** Custom logo path - defaults to BidBro logo */
  logoSrc?: string;
  /** Custom logo alt text */
  logoAlt?: string;
  /** Custom spinner color - defaults to #3577f1 */
  spinnerColor?: string;
  /** Z-index for the overlay */
  zIndex?: number;
  /** Disable blur effect */
  noBlur?: boolean;
  /** Custom background opacity (0-1) */
  backgroundOpacity?: number;
  /** Enable debug mode to see positioning info */
  debug?: boolean;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isVisible,
  text = 'Loading...',
  subtext = 'Please wait while we process your request...',
  textInterval = 3000,
  size = 'medium',
  logoSrc,
  logoAlt = 'BidBro Logo',
  spinnerColor = '#3577f1',
  zIndex = 999999,
  noBlur = false,
  backgroundOpacity = 0.6,
  debug = false
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // State for rotating text
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentSubtextIndex, setCurrentSubtextIndex] = useState(0);

  // Convert text/subtext to arrays if they aren't already
  const textArray = Array.isArray(text) ? text : [text];
  const subtextArray = Array.isArray(subtext) ? subtext : [subtext];

  // Get current text values
  const currentText = textArray[currentTextIndex % textArray.length];
  const currentSubtext = subtextArray[currentSubtextIndex % subtextArray.length];

  // Set up text rotation interval
  useEffect(() => {
    if (isVisible && (textArray.length > 1 || subtextArray.length > 1)) {
      intervalRef.current = setInterval(() => {
        if (textArray.length > 1) {
          setCurrentTextIndex(prev => (prev + 1) % textArray.length);
        }
        if (subtextArray.length > 1) {
          setCurrentSubtextIndex(prev => (prev + 1) % subtextArray.length);
        }
      }, textInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [isVisible, textArray.length, subtextArray.length, textInterval]);

  // Reset indices when overlay becomes visible
  useEffect(() => {
    if (isVisible) {
      setCurrentTextIndex(0);
      setCurrentSubtextIndex(0);
    } else {
      // Clear interval when overlay is hidden
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isVisible]);

  // Get size dimensions
  const getSizeDimensions = () => {
    switch (size) {
      case 'small':
        return {
          container: { width: 80, height: 80 },
          logo: { width: 65, height: 65 },
          spinner: { width: 96, height: 96 },
          textSize: '1rem',
          subtextSize: '0.8rem'
        };
      case 'large':
        return {
          container: { width: 160, height: 160 },
          logo: { width: 125, height: 125 },
          spinner: { width: 184, height: 184 },
          textSize: '1.4rem',
          subtextSize: '1rem'
        };
      default: // medium
        return {
          container: { width: 120, height: 120 },
          logo: { width: 100, height: 100 },
          spinner: { width: 140, height: 140 },
          textSize: '1.2rem',
          subtextSize: '0.9rem'
        };
    }
  };

  const dimensions = getSizeDimensions();

  // Critical inline styles that will always work
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    margin: 0,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `rgba(0, 0, 0, ${backgroundOpacity})`,
    backdropFilter: noBlur ? 'none' : 'blur(2px)',
    zIndex,
    transform: 'none',
    boxSizing: 'border-box',
    transition: 'opacity 0.3s ease-in-out',
    // Debug styles
    ...(debug && {
      border: '5px solid red',
    })
  };

  const contentStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    transform: 'none',
    zIndex: zIndex + 1,
    // Debug styles
    ...(debug && {
      border: '3px solid blue',
      backgroundColor: 'rgba(255, 255, 0, 0.3)',
      boxSizing: 'border-box'
    })
  };

  const logoContainerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    borderRadius: '50%',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    marginBottom: '20px',
    animation: 'pulse 2s ease-in-out infinite',
    flexShrink: 0,
    width: dimensions.container.width,
    height: dimensions.container.height,
  };

  const logoStyle: React.CSSProperties = {
    objectFit: 'contain',
    zIndex: 2,
    display: 'block',
    width: dimensions.logo.width,
    height: dimensions.logo.height,
  };

  const spinnerStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: dimensions.spinner.width,
    height: dimensions.spinner.height,
    border: '4px solid transparent',
    borderTop: `4px solid ${spinnerColor}`,
    borderRight: `4px solid ${spinnerColor}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    zIndex: 1,
  };

  const textStyle: React.CSSProperties = {
    color: 'white',
    fontWeight: 600,
    textAlign: 'center',
    marginTop: '10px',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    position: 'relative',
    zIndex: 10,
    fontSize: dimensions.textSize,
    transition: 'opacity 0.3s ease-in-out',
  };

  const subtextStyle: React.CSSProperties = {
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
    marginTop: '5px',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
    position: 'relative',
    zIndex: 10,
    fontSize: dimensions.subtextSize,
    transition: 'opacity 0.3s ease-in-out',
  };

  // Debug logging
  useEffect(() => {
    if (isVisible && debug) {
      console.group('🔍 LoadingOverlay Debug Info');
      
      // Viewport info
      console.log('📱 Viewport:', {
        width: window.innerWidth,
        height: window.innerHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY
      });

      // Text rotation info
      console.log('📝 Text Rotation:', {
        textArray,
        subtextArray,
        currentTextIndex,
        currentSubtextIndex,
        textInterval,
        currentText,
        currentSubtext
      });

      console.groupEnd();
    }
  }, [isVisible, debug, textArray, subtextArray, currentTextIndex, currentSubtextIndex, textInterval, currentText, currentSubtext]);

  // Prevent body scrolling when overlay is active
  useEffect(() => {
    if (isVisible) {
      const originalStyle = window.getComputedStyle(document.body);
      const originalOverflow = originalStyle.overflow;
      const originalPosition = originalStyle.position;
      
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = '';
        document.body.style.height = '';
      };
    }
  }, [isVisible]);

  if (!isVisible) return null;

  // Use imported logo as default, but allow override with logoSrc prop
  const logoPath = logoSrc || bidbroLogo;

  // Create keyframes animation styles
  const keyframesStyle = `
    @keyframes spin {
      0% { transform: translate(-50%, -50%) rotate(0deg); }
      100% { transform: translate(-50%, -50%) rotate(360deg); }
    }
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    .spinner-ring-inner::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: ${dimensions.spinner.width}px;
      height: ${dimensions.spinner.height}px;
      border: 2px solid transparent;
      border-top: 2px solid rgba(53, 119, 241, 0.3);
      border-radius: 50%;
      animation: spin 2s linear infinite reverse;
    }
  `;

  const overlayContent = (
    <>
      <style>{keyframesStyle}</style>
      <div 
        style={overlayStyle}
        ref={overlayRef}
      >
        <div 
          style={contentStyle}
          ref={contentRef}
        >
          <div style={logoContainerStyle}>
            <div 
              style={spinnerStyle}
              className="spinner-ring-inner"
            ></div>
            <img 
              src={logoPath} 
              alt={logoAlt} 
              style={logoStyle}
              onError={(e) => {
                console.warn('Failed to load logo:', logoPath);
                e.currentTarget.style.display = 'none';
                const container = e.currentTarget.parentElement;
                if (container && !container.querySelector('.logo-fallback')) {
                  const fallback = document.createElement('div');
                  fallback.className = 'logo-fallback';
                  fallback.style.cssText = `
                    color: ${spinnerColor};
                    font-weight: bold;
                    font-size: 1.2rem;
                    text-align: center;
                    z-index: 2;
                    position: relative;
                  `;
                  fallback.textContent = 'BB';
                  container.appendChild(fallback);
                }
              }}
              onLoad={() => {
                console.log('Logo loaded successfully:', logoPath);
              }}
            />
          </div>
          {currentText && <div style={textStyle}>{currentText}</div>}
          {currentSubtext && <div style={subtextStyle}>{currentSubtext}</div>}
          {debug && (
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.8)',
              padding: '10px',
              fontSize: '12px',
              fontFamily: 'monospace',
              borderRadius: '4px',
              zIndex: zIndex + 100
            }}>
              <div>ScrollY: {window.scrollY}px</div>
              <div>ViewportH: {window.innerHeight}px</div>
              <div>ZIndex: {zIndex}</div>
              <div>Size: {size}</div>
              <div>Inline Styles: ✅</div>
              <div>Text Index: {currentTextIndex}/{textArray.length}</div>
              <div>Subtext Index: {currentSubtextIndex}/{subtextArray.length}</div>
              <div>Interval: {textInterval}ms</div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (debug) {
    console.log('🚀 Rendering LoadingOverlay with Portal to body - INLINE STYLES');
  }

  // Use React Portal to render at the root level
  return createPortal(overlayContent, document.body);
};

export default LoadingOverlay;